import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'

import { VideoFullData } from '../../../interfaces'
import { MORE_VIDEOS_QUERY } from '../../../graphql/graphql-queries'
import { Mint, Query_Root } from '../../../graphql/indexer-graphql-types'
import { fbEnrichUsersFromIds, fbEnrichVideosFromIds } from '../../../data/FirebaseEnrich'
import BaseVideoCard from '../card/BaseVideoCard'
import { useInfiniteLoadIndexer } from '../../../hooks/infiniteLoadIndexer'
import { SmallSpinner, SmallSpinnerContainer } from '../../spinner'
import { getMarketplaceData } from '../../../data/IndexerUtils'
import moment from 'moment'
import { idComparer } from '../../../utils/comparer'

const OuterDiv = styled.div<{ sticky: boolean }>`
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  width: 100%;

  @media (min-width: 43em) {
    margin: 25px 0 0 0;
    /* flex-direction: column; */
  }

  @media (min-width: 62em) {
    margin: 0;
    margin-top: ${(props) => (props.sticky ? `337px` : `0`)};
  }

  :after {
    content: '';
    position: fixed;
    z-index: 0;
    pointer-events: none;
    top: 0;
    width: calc(100% + 2px);
    background: -webkit-linear-gradient(
      270deg,
      rgba(16, 16, 16, 1),
      rgba(16, 16, 16, 1),
      rgba(16, 16, 16, 1),
      rgba(16, 16, 16, 0.5),
      rgba(16, 16, 16, 0)
    );

    @media (min-width: 62em) {
      height: 582px;
    }
  }
`

const StyledInfiniteScroll = styled(InfiniteScroll)`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const parseItems = (data: Query_Root | undefined): Mint[] => {
  return data?.mint[0]?.mintRelations
    ?.filter(Boolean)
    .map(({ relatedMintObject }) => relatedMintObject)
    ?.filter(Boolean) as Mint[] | []
}

const enrichItems = async (mints: Mint[]): Promise<VideoFullData[]> => {
  if (!mints.length) {
    return []
  }

  const ids = mints.map((mint) => mint.id)
  const enrichedVideos = await fbEnrichVideosFromIds(ids)
  const creators = await fbEnrichUsersFromIds(enrichedVideos.map((video) => video.created_by))
  return enrichedVideos.map((video) => {
    const mint = mints.find((mint) => mint.id === video.address)
    const creator = creators.find((creator) => creator.address === video.created_by)
    const marketplaceData = mint ? getMarketplaceData(mint) : undefined
    return {
      video,
      creator,
      ...marketplaceData,
    }
  })
}

const MoreVideosFeed = ({ video }: VideoFullData): JSX.Element => {
  const [sticky, setSticky] = React.useState(false)

  const {
    rawData: originalItems,
    data: items,
    fetchNext,
    hasNext,
  } = useInfiniteLoadIndexer<VideoFullData, Mint>({
    id: video.address,
    query: MORE_VIDEOS_QUERY,
    enrichItems,
    parseItems,
    comparer: idComparer,
    variables: { mint: video.address, end: moment(new Date()).add(1, 'month').toISOString() },
    refetchMarker: 'more',
  })

  const isSticky = () => {
    const scrollTop = window.scrollY
    const sticky = scrollTop >= 25 ? true : false
    setSticky(sticky)
  }

  React.useEffect(() => {
    window.addEventListener('scroll', isSticky)
    return () => {
      window.removeEventListener('scroll', isSticky)
    }
  }, [])

  return (
    <OuterDiv sticky={sticky}>
      {originalItems.length === 0 &&
        hasNext &&
        [...Array(6).keys()].map((index) => <BaseVideoCard key={`skeleton-item-${index}`} />)}
      {(items.length > 0 || hasNext) && (
        <StyledInfiniteScroll
          loadMore={fetchNext}
          hasMore={hasNext}
          loader={
            <SmallSpinnerContainer>
              <SmallSpinner />
            </SmallSpinnerContainer>
          }
          useWindow
        >
          {items.map(
            (props: VideoFullData, index: number): JSX.Element => (
              <BaseVideoCard {...props} key={`list-item-${index}`} />
            )
          )}
        </StyledInfiniteScroll>
      )}
    </OuterDiv>
  )
}

export default MoreVideosFeed
